import React from 'react';
import styled, { css, keyframes } from 'styled-components';

export interface BarChartData {
  label: string;
  value: number;
}

interface OutcomesRetentionBarChartProps {
  data: BarChartData[];
  multiple?: number;
  percent?: boolean;
  barHeight?: string;
  barPadding?: string;
  fontSize?: string;
  selectedIndex: number | null;
  setSelectedIndex: (index: number | null) => void;
}

const OutcomesRetentionBarChart: React.FC<OutcomesRetentionBarChartProps> = ({
  data,
  multiple = 1,
  percent = true,
  barHeight = '14px',
  barPadding = '0',
  fontSize = '11px',
  selectedIndex,
  setSelectedIndex,
}) => {
  const handleBarClick = (index: number) => {
    setSelectedIndex(index);
  };

  return (
    <BarContainer>
      {data.map((row, index) => (
        <BarRow key={index} barPadding={barPadding}>
          <BarTitle fontSize={fontSize}>{row.label}</BarTitle>
          <Bar
            barHeight={barHeight}
            isSelected={index === selectedIndex}
            onClick={() => handleBarClick(index)}
          >
            <BarFill percentage={row.value * multiple} isSelected={index === selectedIndex}>
              <span>
                {row.value}
                {percent ? '%' : ''}
              </span>
            </BarFill>
          </Bar>
          <Score>
            {row.value}
            {percent ? '%' : ''}
          </Score>
        </BarRow>
      ))}
    </BarContainer>
  );
};
export default OutcomesRetentionBarChart;

// Styled components
const growAnimation = (percentage: number) => keyframes`
  from {
    width: 0%;
  }
  to {
    width: ${percentage}%;
  }
`;

const BarContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 2;
  gap: 3px;
  padding-left: 0px;
`;

const BarRow = styled.div<{ barPadding: string }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: ${props => props.barPadding}};
`;

const BarTitle = styled.div<{ fontSize: string }>`
  font-family: 'Poppins', sans-serif;
  padding-left: 10px;
  font-size: ${props => props.fontSize}};
  color: #333;
  font-weight: 400;
  flex: 4; 
  text-align: left;
`;

const Score = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  color: #333;
  font-weight: 500;
  flex: 1;
  text-align: right;
`;

const Bar = styled.div<{ barHeight: string; isSelected?: boolean }>`
  position: relative;
  height: ${props => props.barHeight}};

  background-color: #ddd;
  background-color: ${props => (props.isSelected ? '#ccc' : '#ddd')};

  border-radius: 4px;
  overflow: visible;
  flex: 5; 
  cursor: pointer;
  transition: box-shadow 0.1s ease-in-out;
  box-shadow: ${props => (props.isSelected ? '0px 4px 6px rgba(0, 0, 0, 0.5)' : 'none')};
  border: ${props => (props.isSelected ? "1px solid '#ccc'" : 'none')};

  &:hover {
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.3);
  }

  &:hover span {
    opacity: 1;
  }
`;

const BarFill = styled.div<{ percentage: number; isSelected?: boolean }>`
  height: 100%;
  border-radius: 4px;
  background-color: ${props =>
    props.percentage < 40
      ? 'rgba(255, 50, 90, .4)'
      : props.percentage <= 60
        ? 'rgba(255, 216, 111, 1)'
        : 'rgba(129, 199, 132, .7)'};
  animation: ${props => css`
    ${growAnimation(props.percentage)} 0.8s ease-out
  `};
  animation-fill-mode: forwards;
  position: relative;

  display: flex;
  justify-content: center;
  align-items: center;

  span {
    visibility: hidden;
    opacity: 0;
    color: #fff;
    font-family: 'Poppins', sans-serif;
    font-size: 10px;
    font-weight: bold;
    transition: opacity 0.1s ease-in-out;
  }

  &:hover span {
    opacity: 1;
  }
`;
